import {
  SchoolFragment,
  useLicensorsLazyQuery,
  usePathCreatorResourcesLazyQuery
} from "@pathwright/graphql-client/src/generated"
import { useGetNewResource } from "../../path/create/CreateOrSelectResource"
import { massageLicensorsQueryData } from "../../path/create/Licensors"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

// Sets the initial value of the AddPathFlow based on current resources and licensors.
const useAddPathInitialValue = () => {
  const { school } = usePathwrightContext()
  const { initialValue, setInitialValue } = usePathCreatorContext()

  const { getNewResource, loading: loadingNewResource } = useGetNewResource()

  const [licensorsQuery, { loading: loadingLicensors }] =
    useLicensorsLazyQuery()

  const [resourcesQuery, { loading: loadingResources }] =
    usePathCreatorResourcesLazyQuery()

  async function handleSetInitialValue() {
    const promises = [resourcesQuery()]

    let licensors: SchoolFragment[] = []

    // Both schools with curriculum subscriptions and schools with store links will have
    // `is_curriculum_subscriber` set to true. We can therefore skip the query for non-subscriber schools.
    if (school.is_curriculum_subscriber) {
      promises.push(licensorsQuery())
    }

    const [resourcesQueryResult, licensorsQueryResult] = await Promise.all(
      promises
    )

    if (licensorsQueryResult) {
      licensors = massageLicensorsQueryData(licensorsQueryResult.data)
    }

    const hasLicensors = licensors.length > 0

    const hasResources =
      (resourcesQueryResult.data?.resources?.edges?.length || 0) > 0
    // Only set the initial value if it is not already set.
    if (initialValue === undefined) {
      // If there are no licensors and no resources, we set the initial value in such a way so as to select the "create" option.
      if (!hasLicensors && !hasResources) {
        setInitialValue(await getNewResource())
      } else {
        // Selecting null (as opposed to undefined) to allow user to choose between creating new or selecting existing resource.
        setInitialValue(null)
      }
    }
  }

  return {
    initialValue,
    setInitialValue: handleSetInitialValue,
    loading: loadingNewResource || loadingLicensors || loadingResources
  }
}

export default useAddPathInitialValue
